import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDatepickerModule, DateRange } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

const LS_PRESET = 'replay_preset_index';
const LS_SOURCE = 'replay_source_index';
const LS_START  = 'replay_range_start_ms';
const LS_END    = 'replay_range_end_ms';

interface SheetData {
  presetIndex: number;
  sourceIndex: number;
  customRange: { start: Date; end: Date } | null;
}

const SOURCE_OPTIONS = [
  { label: 'All Trips',      icon: 'all' },
  { label: 'My Trips',       icon: 'mine' },
  { label: 'Shared with Me', icon: 'shared' },
];

const PRESET_OPTIONS = [
  { label: 'Last Day',   index: 0 },
  { label: 'Last Week',  index: 1 },
  { label: 'Last Month', index: 2 },
];

@Component({
  selector: 'app-replay-settings-sheet',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDatepickerModule, MatNativeDateModule],
  templateUrl: './replay-settings-sheet.component.html',
  styleUrl: './replay-settings-sheet.component.scss',
})
export class ReplaySettingsSheetComponent implements OnInit {
  activeTab = 0;

  presetIndex: number;
  sourceIndex: number;
  customRange: { start: Date; end: Date } | null;

  saveFilters = true;

  dateRangeError = '';

  // ── Calendar range picker (mirrors Map Display Filter) ──────────────────────
  showCalendar = false;
  minDate = new Date(2020, 0, 1);
  maxDate = new Date();
  selectedRange = new DateRange<Date>(null, null);

  readonly presets = PRESET_OPTIONS;
  readonly sources = SOURCE_OPTIONS;

  constructor(
    private dialogRef: MatDialogRef<ReplaySettingsSheetComponent>,
    @Inject(MAT_DIALOG_DATA) data: SheetData,
  ) {
    this.presetIndex = data.presetIndex;
    this.sourceIndex = data.sourceIndex;
    this.customRange = data.customRange;
  }

  ngOnInit(): void {
    if (this.presetIndex === -1 && this.customRange) {
      this.selectedRange = new DateRange<Date>(this.customRange.start, this.customRange.end);
    }
  }

  get hasChanges(): boolean {
    return true; // Simplified — always allow save
  }

  get customRangeLabel(): string {
    if (!this.customRange) return 'Pick a date range';
    return `${this._fmtDate(this.customRange.start)} – ${this._fmtDate(this.customRange.end)}`;
  }

  selectPreset(index: number): void {
    this.presetIndex = index;
    this.customRange = null;
    this.selectedRange = new DateRange<Date>(null, null);
    this.dateRangeError = '';
  }

  /** Tapping the Custom Range row selects it and opens the full-screen calendar. */
  openCustomRange(): void {
    this.presetIndex = -1;
    if (!this.customRange) {
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 86_400_000);
      this.customRange = { start: weekAgo, end: now };
      this.selectedRange = new DateRange<Date>(weekAgo, now);
    }
    this.dateRangeError = '';
    this.showCalendar = true;
  }

  /** mat-calendar range selection — first tap sets start, second sets end. */
  onDateSelected(date: Date): void {
    const cur = this.selectedRange;
    let next: DateRange<Date>;
    if (!cur.start || cur.end) {
      next = new DateRange<Date>(date, null);
    } else {
      next = date < cur.start ? new DateRange<Date>(date, cur.start) : new DateRange<Date>(cur.start, date);
    }
    this.selectedRange = next;
    // Only a complete range produces a usable customRange (start 00:00, end 23:59:59).
    if (next.start && next.end) {
      this.customRange = { start: this._atStartOfDay(next.start), end: this._atEndOfDay(next.end) };
    } else {
      this.customRange = null;
    }
    this._validateRange();
  }

  closeCalendar(): void { this.showCalendar = false; }
  saveCalendar(): void { this.showCalendar = false; }

  /** Long range label shown above the calendar (e.g. "March 3 – March 9"). */
  getFullDateRange(): string {
    const s = this.selectedRange.start;
    const e = this.selectedRange.end;
    const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
    if (!s) return 'Select date range';
    const ss = s.toLocaleDateString('en-US', opts);
    if (!e || s.getTime() === e.getTime()) return ss;
    return `${ss} – ${e.toLocaleDateString('en-US', opts)}`;
  }

  private _validateRange(): void {
    if (!this.customRange) { this.dateRangeError = ''; return; }
    const diff = (this.customRange.end.getTime() - this.customRange.start.getTime()) / 86_400_000;
    if (diff > 31) {
      this.dateRangeError = 'Cannot select a date range longer than 1 month.';
    } else if (diff < 0) {
      this.dateRangeError = 'End date must be after start date.';
    } else {
      this.dateRangeError = '';
    }
  }

  save(): void {
    const customRange = this.presetIndex === -1 ? this.customRange : null;
    this._saveToStorage(customRange);
    this.dialogRef.close({ presetIndex: this.presetIndex, sourceIndex: this.sourceIndex, customRange });
  }

  private _saveToStorage(customRange: { start: Date; end: Date } | null): void {
    if (!this.saveFilters) return;
    try {
      localStorage.setItem(LS_PRESET, String(this.presetIndex));
      localStorage.setItem(LS_SOURCE, String(this.sourceIndex));
      if (this.presetIndex === -1 && customRange) {
        localStorage.setItem(LS_START, String(customRange.start.getTime()));
        localStorage.setItem(LS_END,   String(customRange.end.getTime()));
      } else {
        localStorage.removeItem(LS_START);
        localStorage.removeItem(LS_END);
      }
    } catch { /* ignore storage errors */ }
  }

  dismiss(): void {
    this.dialogRef.close(null);
  }

  private _atStartOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  }

  private _atEndOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
  }

  private _fmtDate(d: Date): string {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}
